// Protocol state as pushed by the API's WebSocket (see offchain/src/indexer/state.ts), parsed back
// into bigints. The browser no longer reads the chain: live PnL and PAPURR quotes use the exact TypeScript
// mirrors of the contract math.
import type { Address } from 'viem';

import { gainShare, mintAmount, nearEntryCut, pnl } from '../../../offchain/src/math.ts';
import type { ApiPosition } from './relayer.ts';

export type Instrument = {
  assetIndex: number;
  maxLeverage: number;
  priceScale: number;
  maxSpreadBps: number;
  active: boolean;
  paused: boolean;
  maxNotional: bigint;
  winHaircut: bigint;
  bustBuffer: bigint;
  curveA: bigint;
  curveB: bigint;
};

export type Market = {
  id: number;
  inst: Instrument;
  /** Center of the fill band in price units (priceScale per USD), for display; undefined when unusable */
  mid?: bigint;
  /** Fill band: the book mid and the mark, lower first. Buys fill at `high`, sells at `low` (see fillPx) */
  low?: bigint;
  high?: bigint;
  midError?: string;
  oi: { long: bigint; short: bigint };
  caps: { long: bigint; short: bigint };
};

export type Globals = {
  collateral: Address;
  collateralDecimals: number;
  collateralSymbol: string;
  collateralScale: bigint;
  /** USD (18 dec) per HyperCore wei of the collateral */
  coreScale: bigint;
  papurr: Address;
  minMargin: bigint;
  maxMargin: bigint;
  winFeeRate: bigint;
  lossFeeRate: bigint;
  liquidationFeeRate: bigint;
  openPaused: boolean;
  closePaused: boolean;
  houseLiquidity: bigint;
  /** Part of the house liquidity lent on HyperCore's lending market, and the share the owner allows (bps) */
  houseLent: bigint;
  houseLendMaxBps: bigint;
  /**
   * The lending market the house lends into, undefined from an older API or off HyperCore: its supply rate now
   * (bps a year), how much of it is borrowed (bps), and what the house's position is worth there now (USD 18
   * dec; `houseLent` is the value last booked into the house, the difference is yield still to book)
   */
  houseLendRateBps?: number;
  houseLendUtilizationBps?: number;
  houseLentValue?: bigint;
  /**
   * What each withdrawal route can pay now, USD 18 dec: the exchange's USDC on HyperEVM (`withdraw`) and in its
   * HyperCore spot (`withdrawToCore`); the keeper moves money between them. Undefined from an older API.
   */
  poolEvm?: bigint;
  poolCore?: bigint;
  /** Near-entry cut on wins in force now: the band (bps, wider off hours) and the share kept at the entry (WAD) */
  nearEntryBandBps: bigint;
  nearEntryMaxCut: bigint;
  effectiveLp: bigint;
  totalQueued: bigint;
  totalStaked: bigint;
  /** Smallest balance an account may keep staked, PAPURR wei (0 = none) */
  minStake: bigint;
  totalLockedMargin: bigint;
  /** Owed to the PAPURR revenue at the next distribution: its fees plus its share of the house's gain so far (the buyback splits it between stakers and the burn auction) */
  pendingDistribution: bigint;
  /** Staker revenue distributed but not sent to the HYPE buyback yet, USD 18 dec */
  buybackOwed: bigint;
  papurrSupply: bigint;
  /** Effective house liquidity above which its gains are shared with stakers */
  rewardThreshold: bigint;
  /** WAD: share of each loss, of the house's new records above the threshold */
  rewardShares: { loss: bigint; newHigh: bigint };
  /** The house's record: the highest liquidity above the threshold a distribution left it at */
  peakExcess: bigint;
  /** Share of a loss realized now that goes to the PAPURR revenue (stakers and burn auction), WAD */
  stakerShare: bigint;
  maxSessionDuration: bigint;
  /** `fixedRate` > 0: a soft launch, this much PAPURR per USD lost instead of the curve */
  /** `lowHouseMultiple` is absent on a tokenomics from before the low-house ramp (it mints the flat rate under the threshold) */
  mint: { flatThreshold: bigint; flatRate: bigint; tailK: bigint; tailProgress: bigint; fixedRate: bigint; lowHouseMultiple?: bigint };
};

/** Relayer queue: intents waiting, intents settled in the last minute, and the wait that implies */
export type Queue = {
  pending: number;
  submitted: number;
  perMinute: number;
  etaSec: number;
  /** Blocks the waiting intents need; congestion is the level the relayer derives from it */
  blocks?: number;
  congestion?: 'low' | 'medium' | 'high';
  /** Gas of the next blocks, already sent vs still queued, against the per-block budget `blockGas` */
  nextBlocks?: { submitted: number; queued: number }[];
  blockGas?: number;
};

/** Seconds before an intent with `ahead` others in front runs, at the queue's current pace */
export function waitSec(queue: Queue | undefined, ahead: number): number {
  const waiting = queue ? queue.pending + queue.submitted : 0;
  if (!queue || waiting === 0) return 0;
  return Math.ceil((ahead * queue.etaSec) / waiting);
}

/** HouseReserve holdings on HyperCore, core wei (x globals.coreScale: USD 18 dec) */
export type Reserve = { spotWei: bigint; suppliedWei: bigint; totalWei: bigint; minLiquidWei: bigint; maxUtilizationBps: number };

/** HYPE buyback of the PAPURR revenue, core wei: HYPE has 8 decimals on HyperCore, USDC 8 too */
export type Buyback = {
  boughtWei: bigint;
  owedWei: bigint;
  usdcWei: bigint;
  perDayWei: bigint;
  durationSec: bigint;
  /** Order pace: most USDC per order (core wei), seconds between orders, bid bound over the oracle (bps) */
  maxBuyWei: bigint;
  buyIntervalSec: bigint;
  slippageBps: bigint;
  /** Claims paid on HyperEVM: HYPE waiting to be settled, and what the buyback keeps from each (core wei) */
  evmQueuedWei: bigint;
  evmFeeWei: bigint;
  /** Share of every lot set aside for the burn auction, bps; 0 while the auction is off (undefined: an older relayer) */
  auctionBps?: number;
};

/**
 * HYPE -> USDC conversions: the open round and the one being sold (HYPE, core wei), the round ids, the lowest
 * ask of the next order (1e8 units; 0 when unknown), the limits and the totals
 */
export type Converter = {
  openRound: bigint;
  activeRound: bigint;
  creditRound: bigint;
  openWei: bigint;
  activeWei: bigint;
  askPx: bigint;
  slippageBps: number;
  minConvertWei: bigint;
  closeInterval: number;
  maxAttempts: number;
  halted: boolean;
  totalHypeIn: bigint;
  totalUsdcOut: bigint;
  totalHypeBack: bigint;
};

/**
 * PAPURR burn auction: the running epoch (id, start, duration, HYPE in its pot in core wei, PAPURR sold into it
 * in 18 dec), the previous one (and how much of it was bought back), what is on sale now (the epoch it came
 * from, the PAPURR left, that epoch's pot and sold: its price before the mark-up), the buy-back premium and fee
 * (bps; the price and the premium stream to stakers, the fee goes to the dev wallet), and the totals
 */
export type Auction = {
  epoch: bigint;
  startSec: number;
  durationSec: number;
  potWei: bigint;
  soldWei: bigint;
  /** PAPURR sold into the running epoch at each reserve tick (96 entries), and how it would clear if it closed now */
  book: bigint[];
  previewCutoff: number;
  previewClearedWei: bigint;
  /** Core wei per whole PAPURR; 0n when nothing would be taken */
  previewPrice: bigint;
  lastPotWei: bigint;
  lastSoldWei: bigint;
  /** What the previous epoch's close took (paid, then on sale) and its highest tick taken */
  lastClearedWei: bigint;
  lastCutoff: number;
  lastResoldWei: bigint;
  saleEpoch: bigint;
  saleAvailableWei: bigint;
  salePotWei: bigint;
  saleClearedWei: bigint;
  premiumBps: number;
  feeBps: number;
  totalSoldWei: bigint;
  totalResoldWei: bigint;
  totalBurnedWei: bigint;
  totalNotifiedWei: bigint;
  totalProceedsWei: bigint;
  totalPaidWei: bigint;
};

export type Snapshot = {
  block: bigint;
  time: number;
  globals: Globals;
  markets: Market[];
  queue?: Queue;
  reserve?: Reserve;
  buyback?: Buyback;
  auction?: Auction;
  converter?: Converter;
};

export type UserState = {
  balance: bigint;
  walletCollateral: bigint;
  collateralAllowance: bigint;
  papurr: bigint;
  papurrAllowance: bigint;
  staked: bigint;
  /** HYPE earned by the stake and not claimed, core wei (8 decimals) */
  pendingReward: bigint;
  /** Last stake, unstake or claim, unix seconds: 100 days later unclaimed HYPE can be swept to the dev wallet */
  stakingLastActive: number;
  /**
   * Burn auction: PAPURR allowance to it, PAPURR sold into the running epoch and the reserve tick of that sale,
   * HYPE claimable from closed epochs (core wei) and PAPURR those epochs give back (left out by their close)
   */
  auctionAllowance: bigint;
  auctionSold: bigint;
  auctionTick: number;
  auctionClaimable: bigint;
  auctionReturnable: bigint;
  gas: bigint;
  /** HyperCore has an account for the address: withdrawals to Core and HYPE claims revert without one */
  coreUser: boolean;
  /** Claims paid on HyperEVM: HYPE queued for the user, and what it has to withdraw itself (core wei) */
  evmQueued: bigint;
  evmOwed: bigint;
  /** HYPE -> USDC conversions: the user's HYPE in rounds not credited yet, and what it has to withdraw itself (core wei) */
  convertPending: bigint;
  convertOwed: bigint;
};

export type LivePosition = ApiPosition & { gross?: bigint; credited?: bigint };

type J = Record<string, unknown>;
const b = (v: unknown) => BigInt(v as string);

export function parseSnapshot(raw: unknown): Snapshot {
  const s = raw as { block: string; time: number; static: J; globals: J; markets: J[]; queue?: Queue; reserve?: J; buyback?: J; auction?: J; converter?: J };
  const rv = s.reserve;
  const bb = s.buyback;
  const au = s.auction;
  const cv = s.converter;
  const g = s.globals;
  const st = s.static;
  const shares = g.rewardShares as string[];
  const rewardShares = { loss: b(shares[0]), newHigh: b(shares[2]) };
  return {
    block: b(s.block),
    time: s.time,
    queue: s.queue,
    reserve: rv && {
      spotWei: b(rv.spotWei),
      suppliedWei: b(rv.suppliedWei),
      totalWei: b(rv.totalWei),
      minLiquidWei: b(rv.minLiquidWei),
      maxUtilizationBps: Number(rv.maxUtilizationBps),
    },
    buyback: bb && {
      boughtWei: b(bb.boughtWei),
      owedWei: b(bb.owedWei),
      usdcWei: b(bb.usdcWei),
      perDayWei: b(bb.perDayWei),
      durationSec: b(bb.durationSec ?? 7 * 86_400),
      maxBuyWei: b(bb.maxBuyWei ?? 0),
      buyIntervalSec: b(bb.buyIntervalSec ?? 0),
      slippageBps: b(bb.slippageBps ?? 0),
      evmQueuedWei: b(bb.evmQueuedWei ?? 0),
      evmFeeWei: b(bb.evmFeeWei ?? 0),
      auctionBps: bb.auctionBps === undefined ? undefined : Number(bb.auctionBps),
    },
    converter: cv && {
      openRound: b(cv.openRound),
      activeRound: b(cv.activeRound),
      creditRound: b(cv.creditRound),
      openWei: b(cv.openWei),
      activeWei: b(cv.activeWei),
      askPx: b(cv.askPx ?? 0),
      slippageBps: Number(cv.slippageBps),
      minConvertWei: b(cv.minConvertWei),
      closeInterval: Number(cv.closeInterval),
      maxAttempts: Number(cv.maxAttempts),
      halted: cv.halted as boolean,
      totalHypeIn: b(cv.totalHypeIn),
      totalUsdcOut: b(cv.totalUsdcOut),
      totalHypeBack: b(cv.totalHypeBack),
    },
    auction: au && {
      epoch: b(au.epoch),
      startSec: Number(au.startSec),
      durationSec: Number(au.durationSec),
      potWei: b(au.potWei),
      soldWei: b(au.soldWei),
      book: ((au.book as unknown[] | undefined) ?? []).map((x) => b(x)),
      previewCutoff: Number(au.previewCutoff ?? 2 ** 32 - 1),
      previewClearedWei: b(au.previewClearedWei ?? 0),
      previewPrice: b(au.previewPrice ?? 0),
      lastPotWei: b(au.lastPotWei),
      lastSoldWei: b(au.lastSoldWei),
      lastClearedWei: b(au.lastClearedWei ?? 0),
      lastCutoff: Number(au.lastCutoff ?? 2 ** 32 - 1),
      lastResoldWei: b(au.lastResoldWei ?? 0),
      saleEpoch: b(au.saleEpoch ?? 0),
      saleAvailableWei: b(au.saleAvailableWei ?? 0),
      salePotWei: b(au.salePotWei ?? 0),
      saleClearedWei: b(au.saleClearedWei ?? 0),
      premiumBps: Number(au.premiumBps ?? 0),
      feeBps: Number(au.feeBps ?? 0),
      totalSoldWei: b(au.totalSoldWei ?? 0),
      totalResoldWei: b(au.totalResoldWei ?? 0),
      totalBurnedWei: b(au.totalBurnedWei),
      totalNotifiedWei: b(au.totalNotifiedWei),
      totalProceedsWei: b(au.totalProceedsWei ?? 0),
      totalPaidWei: b(au.totalPaidWei),
    },
    globals: {
      collateral: st.collateral as Address,
      collateralDecimals: Number(st.collateralDecimals),
      collateralSymbol: st.collateralSymbol as string,
      collateralScale: b(st.collateralScale),
      coreScale: b(st.coreScale),
      papurr: st.papurr as Address,
      minMargin: b(g.minMargin),
      maxMargin: b(g.maxMargin),
      winFeeRate: b(g.winFeeRate),
      lossFeeRate: b(g.lossFeeRate),
      liquidationFeeRate: b(g.liquidationFeeRate),
      openPaused: g.openPaused as boolean,
      closePaused: g.closePaused as boolean,
      houseLiquidity: b(g.houseLiquidity),
      houseLent: b(g.houseLent ?? 0),
      houseLendMaxBps: b(g.houseLendMaxBps ?? 0),
      poolEvm: g.poolEvm === undefined ? undefined : b(g.poolEvm),
      poolCore: g.poolCore === undefined ? undefined : b(g.poolCore),
      houseLendRateBps: g.houseLendRateBps === undefined ? undefined : Number(g.houseLendRateBps),
      houseLendUtilizationBps: g.houseLendUtilizationBps === undefined ? undefined : Number(g.houseLendUtilizationBps),
      houseLentValue: g.houseLentValue === undefined ? undefined : b(g.houseLentValue),
      nearEntryBandBps: b(g.nearEntryBandBps ?? 0),
      nearEntryMaxCut: b(g.nearEntryMaxCut ?? 0),
      effectiveLp: b(g.effectiveLp),
      totalQueued: b(g.totalQueued),
      totalStaked: b(g.totalStaked),
      minStake: b(g.minStake ?? '0'),
      totalLockedMargin: b(g.totalLockedMargin),
      pendingDistribution:
        b(g.pendingDistribution) +
        gainShare({
          effectiveLp: b(g.effectiveLp),
          threshold: b(g.rewardThreshold),
          peakExcess: b(g.peakExcess),
          newHighShare: rewardShares.newHigh,
        }),
      buybackOwed: b(g.buybackOwed ?? 0),
      papurrSupply: b(g.papurrSupply),
      rewardThreshold: b(g.rewardThreshold),
      rewardShares,
      peakExcess: b(g.peakExcess),
      stakerShare: b(g.stakerShare),
      maxSessionDuration: b(g.maxSessionDuration),
      mint: {
        flatThreshold: b(g.mint_flatThreshold),
        flatRate: b(g.mint_flatRate),
        tailK: b(g.mint_tailK),
        tailProgress: b(g.mint_tailProgress),
        fixedRate: b(g.mint_fixedRate ?? 0),
        ...(g.mint_lowHouseMultiple === undefined ? {} : { lowHouseMultiple: b(g.mint_lowHouseMultiple) }),
      },
    },
    markets: s.markets.map((m) => {
      const i = m.inst as J;
      const oi = m.oi as J;
      const caps = m.caps as J;
      return {
        id: Number(m.id),
        inst: {
          assetIndex: Number(i.assetIndex),
          maxLeverage: Number(i.maxLeverage),
          priceScale: Number(i.priceScale),
          maxSpreadBps: Number(i.maxSpreadBps),
          active: i.active as boolean,
          paused: i.paused as boolean,
          maxNotional: b(i.maxNotional),
          winHaircut: b(i.winHaircut),
          bustBuffer: b(i.bustBuffer),
          curveA: b(i.curveA),
          curveB: b(i.curveB),
        },
        mid: m.mid === undefined ? undefined : b(m.mid),
        low: m.low === undefined ? undefined : b(m.low),
        high: m.high === undefined ? undefined : b(m.high),
        midError: m.midError as string | undefined,
        oi: { long: b(oi.long), short: b(oi.short) },
        caps: { long: b(caps.long), short: b(caps.short) },
      };
    }),
  };
}

export function parseWallet(raw: unknown): UserState {
  const w = raw as J;
  return {
    balance: b(w.balance),
    walletCollateral: b(w.walletCollateral),
    collateralAllowance: b(w.collateralAllowance),
    papurr: b(w.papurr),
    papurrAllowance: b(w.papurrAllowance),
    staked: b(w.staked),
    pendingReward: b(w.pendingReward),
    stakingLastActive: Number(b(w.stakingLastActive ?? 0)),
    auctionAllowance: b(w.auctionAllowance ?? 0),
    auctionSold: b(w.auctionSold ?? 0),
    auctionTick: Number(w.auctionTick ?? 0),
    auctionClaimable: b(w.auctionClaimable ?? 0),
    auctionReturnable: b(w.auctionReturnable ?? 0),
    gas: b(w.gas),
    coreUser: w.coreUser as boolean,
    evmQueued: b(w.evmQueued ?? 0),
    evmOwed: b(w.evmOwed ?? 0),
    convertPending: b(w.convertPending ?? 0),
    convertOwed: b(w.convertOwed ?? 0),
  };
}

/**
 * The price a trade fills at now: buys (long opens, short closes) at the top of the band, sells at the bottom,
 * the worse of the book mid and the mark for the trader (PapurrExchange._band)
 */
export function fillPx(m: Market | undefined, buy: boolean): bigint | undefined {
  return buy ? (m?.high ?? m?.mid) : (m?.low ?? m?.mid);
}

/** PnL if closed now, at the side of the band a close fills at: what `previewClose` returns, computed locally */
export function livePositions(ps: ApiPosition[], markets: Market[] | undefined, g?: Globals): LivePosition[] {
  return ps.map((p) => {
    const m = markets?.find((x) => x.id === p.instrumentId);
    // closing a long sells, closing a short buys back
    const exit = fillPx(m, !p.isLong);
    if (!m || !exit) return { ...p };
    const r = pnl({ entryPx: p.entryPx, exitPx: exit, margin: p.margin, leverage: p.leverage, isLong: p.isLong }, m.inst);
    // a win this close to the entry gives the house its near-entry cut, as the contract does on close
    const cut = r.credited > 0n && g ? nearEntryCut(r.credited, p.entryPx, exit, g.nearEntryBandBps, g.nearEntryMaxCut) : 0n;
    return { ...p, gross: r.gross, credited: r.credited - cut };
  });
}

/** PAPURR a loss of `usd` (net of fees on a close, the margin on a liquidation) would mint now (PapurrTokenomics.quoteMint) */
export function quotePapurr(usd: bigint, g: Globals): bigint | undefined {
  if (usd <= 0n) return undefined;
  return mintAmount({ lp: g.effectiveLp, amount: usd, ...g.mint }).minted;
}
